import axios from "../utils/interceptor";
import React, { useRef, useState, ChangeEvent, useEffect } from "react";
import "../styles/documentor-ai.css";
import { analyzeDocument, processEditor } from "../utils/urls";
import CustomCKEditor from "../components/CustomCKEditor";
import { useNavigate } from "react-router-dom";
import mammoth from "mammoth";
import PDFViewerModal from "../components/pdfViwer";
import * as pdfjsLib from "pdfjs-dist";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import { BsLightbulb, BsCheckAll, BsXCircle } from "react-icons/bs";

// Initialize PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.54/build/pdf.worker.min.mjs`;

// ✅ Define types at top level
interface Section {
  id: number;
  title: string;
  content: string;
  level: string;
}

const DocumentorAI = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [htmlData, setHtmlData] = useState("");
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documentMetadata, setDocumentMetadata] = useState({
    headers: {},
    footers: {},
  });
  // Section state
  const [sections, setSections] = useState<Section[]>([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState<number[]>([]);
  const [uploadedDoc, setUploadedDoc] = useState<any>(null);

  // Cleanup URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Trigger file input
  const triggerFileInput = () => {
    inputRef.current?.click();
  };

  // Extract sections from backend for DOCX files
  const extractSectionsFromBackend = async (file: File): Promise<Section[]> => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(`/split-docx`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setDocumentMetadata({
      headers: response.data.headers || {},
      footers: response.data.footers || {},
    });

    return response.data.sections.map((sec: any, index: number) => ({
      id: index + 1,
      title: sec.heading,
      content: sec.content,

      level: sec.heading.match(/^\s*(\d+(?:\.\d+)+)/)?.[1] || "0.0",
    }));
  };

  const editorConfiguration = {
    htmlSupport: {
      allow: [
        {
          name: "img",
          attributes: {
            src: /.*/, // ✅ Allow any src (including data:image)
            alt: true,
            title: true,
            style: true,
          },
          classes: true,
        },
      ],
    },
  };

  // Handle file upload
  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    let htmlContent;
    // Reset previous state
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfData(null);
    setIsPdfModalOpen(false);
    setHtmlData("");
    setSections([]);
    setSelectedSectionIds([]);

    const isDocx = file.name.toLowerCase().endsWith(".docx");
    const isPdf = file.name.toLowerCase().endsWith(".pdf");

    if (!isDocx && !isPdf) {
      alert("Please upload a .docx or .pdf file.");
      return;
    }

    try {
      setLoadingDoc(true);

      if (isDocx) {
        // Process DOCX file using the backend to ensure consistency
        setUploadedDoc(file);
        const extractedSections = await extractSectionsFromBackend(file);

        if (extractedSections.length > 0) {
          // Combine content from all sections to display in the editor
          const fullHtmlContent = extractedSections
            .map((s) => s.content)
            .join("");
          setHtmlData(fullHtmlContent);
          setSections(extractedSections);
          setSelectedSectionIds(extractedSections.map((s) => s.id));
        } else {
          // Handle case with no sections
          setHtmlData("");
          setSections([]);
        }
      } else if (isPdf) {
        // Process PDF file
        const formData = new FormData();
        formData.append("file", file);

        try {
          // Call backend to convert PDF → DOCX → HTML + Section Split
          const response = await axios.post(`/split-pdf`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          const { html, sections: pdfSections } = response.data;

          // Put the HTML into CKEditor
          setHtmlData(html || "");

          // Set extracted sections in right panel
          if (pdfSections?.length > 0) {
            const sectionsWithIds = pdfSections.map(
              (s: any, index: number) => ({
                id: index + 1,
                title: s.heading || `Section ${index + 1}`,
                content: s.content || "",
                level: s.level || "1.0",
              })
            );

            setSections(sectionsWithIds);
            setSelectedSectionIds(sectionsWithIds.map((s: Section) => s.id));
          } else {
            // fallback: whole doc as one section
            setSections([
              {
                id: 1,
                title: "Full Document",
                content: html || "",
                level: "1.0",
              },
            ]);
            setSelectedSectionIds([1]);
          }
        } catch (error) {
          console.error("PDF processing failed:", error);
          alert("Failed to process PDF. Please try again.");
        }
      }
    } catch (error) {
      console.error("File processing failed:", error);
      alert("Failed to process file. Please try again.");
    } finally {
      setLoadingDoc(false);
      if (event.target) event.target.value = "";
    }
  };

  // ✅ Select All Sections
  const handleSelectAll = () => {
    setSelectedSectionIds(sections.map((s) => s.id));
  };

  // ✅ Deselect All Sections
  const handleDeselectAll = () => {
    setSelectedSectionIds([]);
  };

  // Send selected sections to backend
  const fetchSuggestion = async () => {
    setLoading(true);
    try {
      let selectedContents: string[] = [];

      if (sections.length > 0 && selectedSectionIds.length > 0) {
        const selectedSections = sections.filter((s) =>
          selectedSectionIds.includes(s.id)
        );
        selectedContents = selectedSections.map((s) => s.content);
      } else if (htmlData.trim() !== "") {
        selectedContents = [htmlData];
      } else {
        alert("Please upload a document or enter content to analyze.");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        processEditor,
        { pages: selectedContents },
        { headers: { "Content-Type": "application/json" } }
      );

      navigate("/results", {
        state: {
          suggestion: response.data,
          selectedSections:
            sections.length > 0 && selectedSectionIds.length > 0
              ? sections
                  .filter((s) => selectedSectionIds.includes(s.id))
                  .map((s) => s.title)
              : ["Custom Input"],
          headers: documentMetadata.headers,
          footers: documentMetadata.footers,
        },
      });
    } catch (err) {
      console.error("Analysis failed:", err);
      alert("Failed to analyze content.");
    } finally {
      setLoading(false);
    }
  };

  const analyzeWithAI = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("file", uploadedDoc);
    const response = await axios.post(analyzeDocument, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setLoading(false);

    navigate("/results", {
      state: {
        suggestion: response.data,
        selectedSections:
          sections.length > 0 && selectedSectionIds.length > 0
            ? sections
                .filter((s) => selectedSectionIds.includes(s.id))
                .map((s) => s.title)
            : ["Custom Input"],
        headers: documentMetadata.headers,
        footers: documentMetadata.footers,
      },
    });
  };
  return (
    <>
      {/* Loading Overlays */}
      {loadingDoc && (
        <div className="loading-overlay">
          <LoadingSpinner content="Loading Your Document.." />
        </div>
      )}
      {loading && (
        <div className="loading-overlay">
          <LoadingSpinner content="Analyzing..." />
        </div>
      )}

      {/* Main Layout */}
      <div
        className="documentor-container"
        style={{ display: "flex", height: "calc(100vh - 60px)" }}
      >
        {/* Left: Editor */}
        <div
          style={{ flex: 3, padding: "10px", borderRight: "1px solid #ddd" }}
        >
          <div className="top-bar">
          <p><strong>Note:</strong> Document processing supports up to 15 pages per file</p>
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ marginBottom: "10px" }}
            >
              <div className="button-group d-flex gap-2">
                {/* File Upload Button */}
                <button
                  onClick={triggerFileInput}
                  className="btn btn-primary d-flex align-items-center gap-1"
                  type="button"
                >
                  Upload Document
                </button>

                <input
                  type="file"
                  accept=".docx,.pdf"
                  ref={inputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />

                <button
                  onClick={analyzeWithAI}
                  className="btn btn-success d-flex align-items-center gap-1"
                  disabled={htmlData === ""}
                  type="button"
                >
                  Regenerate With AI
                </button>
              </div>
            </div>
          </div>

          <CustomCKEditor
            data={htmlData}
            onChange={(event: any, editor: any) => {
              const data = editor.getData();
              setHtmlData(data);
            }}
            config={editorConfiguration}
          />
        </div>

        {/* Right: Section Selector */}
        <div
          style={{
            flex: 1,
            padding: "20px",
            overflowY: "auto",
            background: "#f8f9fa",
            borderLeft: "1px solid #ddd",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <h5 style={{ color: "#333", margin: 0 }}>Sections to Analyze</h5>
          </div>

          {/* Select/Deselect All Buttons */}
          {sections.length > 0 && (
            <div style={{ marginBottom: "15px", display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={handleSelectAll}
                className="btn btn-sm btn-outline-primary"
                style={{ fontSize: "12px", padding: "4px 8px" }}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleDeselectAll}
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: "12px", padding: "4px 8px" }}
              >
                Deselect All
              </button>
            </div>
          )}

          {/* Section List */}
          {sections.length > 0 ? (
            <ul style={{ listStyle: "none", padding: 0 }}>
              {sections.map((section) => (
                <li key={section.id} style={{ marginBottom: "10px" }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedSectionIds.includes(section.id)}
                      onChange={() => {
                        setSelectedSectionIds((prev) =>
                          prev.includes(section.id)
                            ? prev.filter((id) => id !== section.id)
                            : [...prev, section.id]
                        );
                      }}
                      style={{ marginRight: "8px" }}
                    />
                    <span>{section.title}</span>
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ color: "#666", fontSize: "14px" }}>
              Upload a document to see sections.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default DocumentorAI;
